<script setup lang="ts">
import { ref, onMounted } from 'vue'
import i18n from '@/localization/i18n'
import { getProRatings } from '@/services/modules/WorkerService'
import RatingStars from '@/views/components/RatingStars.vue'
import moment from '@/utils/moment'

const t = i18n.global.t

const props = defineProps<{
    account_id: number
}>()

type Rating = {
    id: number
    client_first_name: string
    client_last_name: string
    score: number
    comment: string | null
    posted: string
}

type RatingsData = {
    count: number
    total_pages: number
    current_page_number: number
    next: string | null
    previous: string | null
    average: number
    total: number
    distribution: Record<string, number>
    items: Rating[]
}

const ratingsData = ref<RatingsData | null>(null)
const allItems = ref<Rating[]>([])
const loadingMore = ref(false)
const loading = ref(true)
const error = ref(false)
const PAGE_SIZE = 5

const fetchRatings = (page: number) => {
    if (page === 1) {
        loading.value = true
        allItems.value = []
    } else {
        loadingMore.value = true
    }
    error.value = false
    getProRatings(props.account_id, page, PAGE_SIZE)
        .then((response) => {
            ratingsData.value = response.data
            allItems.value = [...allItems.value, ...response.data.items]
            loading.value = false
            loadingMore.value = false
        })
        .catch(() => {
            error.value = true
            loading.value = false
            loadingMore.value = false
        })
}

onMounted(() => fetchRatings(1))

const loadMore = () => {
    if (!ratingsData.value) return
    fetchRatings(ratingsData.value.current_page_number + 1)
}

const formatDate = (date: string) => moment(date, 'DD/MM/YYYY').format('D MMMM YYYY')

const distributionKeys = ['5', '4', '3', '2', '1']

const distributionPercent = (key: string): number => {
    if (!ratingsData.value || ratingsData.value.total === 0) return 0
    return Math.round(((ratingsData.value.distribution[key] ?? 0) / ratingsData.value.total) * 100)
}
</script>

<template>
    <div v-if="!loading && ratingsData" class="card h-100">
        <div class="card-body">
            <div class="h-style h3-style use-poppins mb-75">{{ t('w_txt_pro_ratings_title') }}</div>

            <!-- État vide : aucun avis -->
            <div v-if="ratingsData.total === 0" class="d-flex align-items-center justify-content-center py-4">
                <p class="card-text fw-light fst-italic text-muted mb-0">{{ t('w_txt_pro_ratings_none') }}</p>
            </div>

            <!-- Résumé -->
            <template v-else>
                <div class="row mt-2 mb-3">
                    <div class="col-md-4 d-flex flex-column align-items-center justify-content-center mb-3 mb-md-0">
                        <div class="h-style h1-style use-poppins">{{ ratingsData.average.toFixed(1) }}<span class="h-style h5-style use-poppins fw-light">/5</span></div>
                        <RatingStars :value="ratingsData.average" class="mt-1" />
                        <p class="card-text fw-light fst-italic mt-1">{{ t('w_txt_pro_ratings_total', { total: ratingsData.total }) }}</p>
                    </div>
                    <div class="col-md-8 d-flex flex-column justify-content-center">
                        <div v-for="key in distributionKeys" :key="key" class="d-flex align-items-center mb-1">
                            <span class="me-1 h-style h6-style use-poppins">{{ key }}</span>
                            <span class="material-symbols-rounded me-1" style="font-size: 16px; color: #fbbc04;">star</span>
                            <div class="progress flex-fill mx-2" style="height: 8px;">
                                <div class="progress-bar bg-warning" :style="{ width: distributionPercent(key) + '%' }"></div>
                            </div>
                            <span class="h-style h6-style use-poppins fw-light">{{ ratingsData.distribution[key] ?? 0 }}</span>
                        </div>
                    </div>
                </div>

                <!-- Liste des avis cumulés -->
                <div v-for="rating in allItems" :key="rating.id" class="border-top pt-2 mt-2">
                    <div class="row">
                        <div class="col-md-4">
                            <p class="card-text mb-0"><strong>{{ rating.client_first_name }} {{ rating.client_last_name.charAt(0).toUpperCase() }}.</strong></p>
                            <p class="card-text fw-light fst-italic mb-0">{{ formatDate(rating.posted) }}</p>
                            <p class="card-text text-success mb-0">
                                <span class="material-symbols-rounded me-1" style="font-size: 14px; vertical-align: middle;">verified</span>{{ t('w_txt_pro_ratings_verified_client') }}
                            </p>
                        </div>
                        <div class="col-auto px-0"><div class="vr h-100"></div></div>
                        <div class="col ps-3">
                            <div class="d-flex align-items-center mb-1">
                                <RatingStars :value="rating.score" />
                                <span class="h-style h6-style use-poppins ms-1">{{ rating.score }}/5</span>
                            </div>
                            <p v-if="rating.comment" class="card-text mb-0">{{ rating.comment }}</p>
                            <p v-else class="card-text fw-light fst-italic mb-0">{{ t('w_txt_pro_ratings_no_comment') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Bouton Voir plus -->
                <div v-if="ratingsData.next" class="border-top pt-2 mt-2 text-center">
                    <button @click="loadMore" :disabled="loadingMore" class="btn btn-primary waves-effect">
                        <span v-if="!loadingMore">{{ t('w_act_pro_ratings_load_more') }}</span>
                        <div v-else class="spinner-border spinner-border-sm text-light" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>
